<template>
  <div class="auth-shell">
    <div class="auth-layout">
      <div class="auth-left" aria-hidden="true">
        <!-- Left side now empty for cleaner look -->
      </div>

      <div class="auth-right">
        <div class="auth-card">
          <div class="auth-card-header">
            <div class="text-center mb-6">
              <h1 class="font-bold text-slate-900 mb-2" style="font-size: 2rem; line-height: 1;">EzModal</h1>
            </div>
            <div class="auth-card-eyebrow">Welcome back</div>
            <h2 class="auth-card-title">Sign in</h2>
          </div>

          <form @submit.prevent="handleSubmit" class="auth-form" novalidate>
            <label class="auth-label" for="email">E-Mail Address</label>
            <div>
              <input
                id="email"
                class="uk-input auth-input"
                type="email"
                v-model="email"
                required
                autofocus
                autocomplete="email"
                inputmode="email"
                :class="{ 'uk-form-danger': formError }"
              />
            </div>

            <label class="auth-label" for="password">Password</label>
            <div>
              <input
                id="password"
                class="uk-input auth-input"
                type="password"
                v-model="password"
                required
                autocomplete="current-password"
              />
            </div>

            <button type="submit" class="uk-button uk-button-primary auth-submit">Login</button>

            <div v-if="formError" class="uk-form-danger auth-error" role="alert" aria-live="polite">
              <p>{{ formError }}</p>
            </div>
          </form>
          
          <div class="mt-6 text-center text-sm text-slate-600">
            Don't have an account? <a href="/register" class="text-sky-500 hover:text-sky-600 font-semibold transition-colors duration-150">Sign up</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import LOGIN from '@/gql/auth/Login.gql';
import { useAuthStore } from '@/stores/auth';


export default defineComponent({
  name: 'Login',
  data() {
    return {
      email: '' as string,
      password: '' as string,
      formError: null as string | null,
    };
  },
  watch: {
    email(value: string) {
      this.email = this.lowercase(value);
    },
  },
  methods: {
    lowercase(value: string) {
      if (!value) return '';
      return value.toLowerCase();
    },
    async handleSubmit() {
      try {
        const authStore = useAuthStore();
        const response = await (this as any).$apollo.mutate({
          mutation: LOGIN,
          variables: {
            email: this.email,
            password: this.password,
          },
        });

        const accessToken = response?.data?.auth?.accessToken;
        const refreshToken = response?.data?.auth?.refreshToken;
        const username = response?.data?.auth?.username;
        const isAdmin = response?.data?.auth?.isAdmin;
        const isSuperuser = response?.data?.auth?.isSuperuser;

        // Store is the UI source of truth, localStorage is persistence only
        authStore.setSession({
          accessToken,
          refreshToken,
          username,
          email: this.email,
          isAdmin,
          isSuperuser,
        });

        this.formError = null;
        this.$emit('logIn');


        this.$router.push('/tracking');

      } catch (error: any) {
        console.error('Apollo error:', error);

        if (error.graphQLErrors?.length > 0) {
          this.formError = error.graphQLErrors[0]?.message || 'GraphQL Error';
        } else if (error.networkError?.result?.errors?.length > 0) {
          this.formError = error.networkError.result.errors[0]?.message || 'Network Error';
        } else if (error.networkError?.message) {
          this.formError = error.networkError.message;
        } else {
          this.formError = error.message || 'Unexpected server response';
        }
      }
    },
  },
});
</script>

<style scoped>
.auth-shell {
  height: calc(100vh - 80px);
  overflow: hidden;
  background: #f8fafc;
  padding: 0;
  display: block;
}

.auth-layout {
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: none;
  box-shadow: none;
  background: transparent;
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.auth-left {
  position: relative;
  height: 100%;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  background-image: url('~@/assets/auth/login-hero.jpg');
}

.auth-left-content {
  position: absolute;
  left: 42px;
  right: 42px;
  bottom: 40px;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.auth-left-title {
  margin: 14px 0 8px;
  font-size: 34px;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: #ffffff;
}

.auth-left-subtitle {
  margin: 0;
  max-width: 42ch;
  font-size: 15px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.9);
}

.auth-right {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: white;
  height: 100%;
  overflow-y: auto;
}

.auth-card {
  width: min(420px, 100%);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.5);
  background: #ffffff;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.auth-card-header {
  margin-bottom: 18px;
}

.auth-card-eyebrow {
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 6px;
}

.auth-card-title {
  margin: 0;
  font-size: 24px;
  letter-spacing: -0.025em;
  color: #0f172a;
}

.auth-form {
  display: grid;
  gap: 10px;
}

.auth-label {
  margin-top: 6px;
  font-size: 12px;
  color: #475569;
}

.auth-input {
  margin: 0;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.auth-input:focus {
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05);
}

.auth-submit {
  margin-top: 10px;
  border-radius: 12px;
  font-weight: 600;
}

.auth-error {
  margin-top: 6px;
}

.auth-error p {
  margin: 0;
}

@media (max-width: 960px) {
  .auth-shell {
    height: calc(100vh - 80px);
  }

  .auth-layout {
    grid-template-columns: 1fr;
    height: auto;
    min-height: calc(100vh - 80px);
  }

  .auth-left {
    position: relative;
    height: 280px;
  }

  .auth-left-content {
    left: 22px;
    right: 22px;
    bottom: 20px;
  }

  .auth-right {
    padding: 20px;
    height: auto;
    overflow-y: visible;
  }
}
</style>